import * as Sentry from "@sentry/vue";
import {
  VueQueryPlugin,
  type VueQueryPluginOptions,
} from "@tanstack/vue-query";
import { createApp } from "vue";
import { createVfm } from "vue-final-modal";
import Toast, { type PluginOptions, POSITION } from "vue-toastification";
import "vue-toastification/dist/index.css";

import "@/index.css";
import App from "@/App.vue";
import { i18nInstance } from "@/lang";
import growthbook from "@/plugins/growthbook";
import http from "@/plugins/http";
import router from "@/plugins/router";
import segment from "@/plugins/segment";

import "vue-final-modal/style.css";

const toastOptions: PluginOptions = {
  transition: "Vue-Toastification__fade",
  maxToasts: 5,
  newestOnTop: true,
  position: POSITION.TOP_RIGHT,
  hideProgressBar: true,
  timeout: 5000,
  closeOnClick: true,
  closeButton: "button",
  icon: true,
  rtl: false,
  filterBeforeCreate: (toast, toasts) => {
    if (toasts.filter((t) => t.type === toast.type).length !== 0) {
      // Returning false discards the toast
      return false;
    }

    // You can modify the toast if you want
    return toast;
  },
};

const queryClientConfig: VueQueryPluginOptions = {
  enableDevtoolsV6Plugin: true,
  queryClientConfig: {
    defaultOptions: {
      queries: {
        staleTime: 1000 * 10, // 1000ms * 10
      },
    },
  },
};

const vfm = createVfm();

const app = createApp(App)
  .use(router)
  .use(i18nInstance)
  .use(vfm)
  .use(Toast, toastOptions)
  .use(segment)
  .use(growthbook)
  .use(http)
  .use(VueQueryPlugin, queryClientConfig);

Sentry.init({
  app,
  enabled: import.meta.env.VITE_SENTRY_ENABLED === "true",
  dsn: "https://74a8ce00d2e27a05a5ca7d666e275bd5@o1000763.ingest.sentry.io/4506361151488000",
  tunnel: import.meta.env.VITE_SENTRY_TUNNEL,
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],

  tracesSampleRate: 0.1,

  // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    "localhost",
    /^https:\/\/api\.matchwornshirt\.com/,
    /^https:\/\/api\.mwsprod\.com/,
    /^https:\/\/api\.mwsacc\.com/,
  ],

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  beforeSend(event) {
    const messagesToIgnore = [
      "Expected to not be in Angular Zone",
      'Couldn\'t resolve component "default" at',
    ];
    if (event.message && messagesToIgnore.includes(event.message)) {
      return null;
    }

    // Before the request completes, the value of status is 0. Browsers also report a status of 0 in case of XMLHttpRequest errors.
    // We should be able to safely ignore status code 0 errors.
    // https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/status
    // here we check the last breadcrumb to see if the status code is 0
    if (event.breadcrumbs?.at(-1)?.data?.status_code === 0) {
      return null;
    }

    return event;
  },
});

Promise.all([router.isReady(), segment.isReady()]).then(() => {
  app.mount("#app");
  segment.afterInstall();

  // Growthbook depends on segment
  app.runWithContext(() => {
    growthbook.afterInstall();
  });
});

const reloadRouter = () => {
  const currentPage = new URL(window.location.href);

  // Reload only when we havent reloaded yet, so we don't fall in a redirect loop
  if (currentPage.searchParams.get("reloaded") === null) {
    currentPage.searchParams.set("reloaded", "true");
    window.history.pushState({}, "", currentPage.toString());

    // Reload to get new assets in
    window.location.reload();
  } else if (currentPage.searchParams.get("reloaded") === "true") {
    currentPage.searchParams.delete("reloaded");
    window.history.pushState({}, "", currentPage.toString());
  }
};

router.onError(() => {
  reloadRouter();
});

window.addEventListener("vite:preloadError", (event) => {
  // Disable the error
  event.preventDefault();
  reloadRouter();
});
